import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Mail, Clock, TrendingUp, Users, Heart, Zap } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface DailyReportPreferences {
  id?: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

const DailyReportSettings: React.FC = () => {
  const { user, organizationId } = useAuth();
  const [preferences, setPreferences] = useState<DailyReportPreferences>({ enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isBusinessAdmin, setIsBusinessAdmin] = useState(false);

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user?.id) return;

      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        setIsBusinessAdmin(profile?.role === 'bizadmin');
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };

    checkUserRole();
  }, [user?.id]);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user?.id || !organizationId || !isBusinessAdmin) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('daily_report_preferences')
          .select('*')
          .eq('user_id', user.id)
          .eq('organization_id', organizationId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw error;
        }

        if (data) {
          setPreferences(data);
        } else {
          // No preferences found, use defaults
          setPreferences({ enabled: true });
        }
      } catch (error) {
        console.error('Error fetching daily report preferences:', error);
        toast.error('Failed to load email preferences');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [user?.id, organizationId, isBusinessAdmin]);

  const handleToggleEnabled = async () => {
    if (!user?.id || !organizationId) return;

    setSaving(true);
    try {
      const newEnabled = !preferences.enabled;
      
      if (preferences.id) {
        // Update existing record
        const { error } = await supabase
          .from('daily_report_preferences')
          .update({ enabled: newEnabled })
          .eq('id', preferences.id);

        if (error) throw error;
      } else {
        // Create new record
        const { data, error } = await supabase
          .from('daily_report_preferences')
          .insert({
            user_id: user.id,
            organization_id: organizationId,
            enabled: newEnabled
          })
          .select()
          .single();

        if (error) throw error;
        setPreferences(data);
      }

      setPreferences(prev => ({ ...prev, enabled: newEnabled }));
      toast.success(
        newEnabled 
          ? 'Daily reports enabled! You\'ll receive your first report tomorrow morning.' 
          : 'Daily reports disabled successfully.'
      );
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update email preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!user?.id || !organizationId) return;

    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('send-daily-reports', {
        body: {
          test: true,
          userId: user.id,
          organizationId: organizationId
        }
      });

      if (error) throw error;
      
      toast.success('Test email sent! Check your inbox in a few minutes.');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  if (!isBusinessAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Daily Intelligence Reports
          </CardTitle>
          <CardDescription>
            Daily email reports are only available for Business Administrators.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              You need Business Administrator privileges to access daily email reports. 
              Contact your organization administrator to upgrade your access level.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Daily Intelligence Reports
          </CardTitle>
          <CardDescription>Loading your email preferences...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Daily Intelligence Reports
          </CardTitle>
          <CardDescription>
            Get comprehensive business insights delivered to your inbox every morning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="daily-reports-enabled" className="text-base font-medium">
                Enable Daily Reports
              </Label>
              <p className="text-sm text-gray-600">
                Receive "While You Were Sleeping" reports every morning at 5 AM ET
              </p>
            </div>
            <Switch
              id="daily-reports-enabled"
              checked={preferences.enabled}
              onCheckedChange={handleToggleEnabled}
              disabled={saving}
            />
          </div>

          {preferences.enabled && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                Delivered daily at 5:00 AM Eastern Time
              </div>
              
              <Button 
                variant="outline" 
                onClick={handleTestEmail}
                disabled={saving}
                className="w-full sm:w-auto"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending Test Email...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* What's Included Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What's Included in Your Daily Report</CardTitle>
          <CardDescription>
            AI-powered insights to save you time, grow revenue, and keep customers happy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Time Savers */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Zap className="h-4 w-4 text-blue-600" />
                </div>
                <h4 className="font-medium text-blue-900">Save Time</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Automation opportunities</li>
                <li>• Tasks ready for delegation</li>
                <li>• Process improvements</li>
                <li>• Priority action items</li>
              </ul>
            </div>

            {/* Revenue Growth */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <h4 className="font-medium text-green-900">Grow Revenue</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Upsell opportunities</li>
                <li>• Pricing insights</li>
                <li>• Retention strategies</li>
                <li>• Program optimization</li>
              </ul>
            </div>

            {/* Customer Happiness */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Heart className="h-4 w-4 text-yellow-600" />
                </div>
                <h4 className="font-medium text-yellow-900">Delight Customers</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• At-risk student alerts</li>
                <li>• Birthday celebrations</li>
                <li>• Engagement opportunities</li>
                <li>• Service improvements</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Sources Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Comprehensive Business Intelligence</CardTitle>
          <CardDescription>
            Your reports analyze data from across your entire operation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm">Revenue</div>
              <div className="text-xs text-gray-600">Trends & insights</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm">Students</div>
              <div className="text-xs text-gray-600">Engagement & risk</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm">Staff</div>
              <div className="text-xs text-gray-600">Performance & scheduling</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm">Operations</div>
              <div className="text-xs text-gray-600">Efficiency & events</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyReportSettings;